import { ReactNode } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import type { Tone } from "../Pill";
import s from "./Notice.module.scss";

const ICON = { info: InfoOutlinedIcon, neutral: InfoOutlinedIcon, ok: CheckCircleOutlineRoundedIcon, warning: WarningAmberRoundedIcon, danger: ErrorOutlineRoundedIcon };

/**
 * Inline message. Danger is an alert (announced immediately); others are polite status.
 * `live={false}`: no role of its own, for a notice put inside a live region that is already there (filled, not mounted).
 * `dismiss`: a ✕ (IconButton) that takes the message away. It keeps its own place at the end of the first line; on a
 * phone the actions move to a full row of their own, the ✕ does not.
 */
export const Notice = ({ tone = "info", title, action, dismiss, children, live = true, centred = false }: { tone?: Tone; title?: ReactNode; action?: ReactNode; dismiss?: ReactNode; children?: ReactNode; live?: boolean; /** Icon, words and button share one centre line (a one-line message with a button beside it). */ centred?: boolean }) => {
  const Icon = ICON[tone];
  return (
    <div className={`${s.notice} ${s[tone]}${centred ? ` ${s.centred}` : ""}`} role={!live ? undefined : tone === "danger" ? "alert" : "status"}>
      <Icon aria-hidden className={s.icon} />
      <div className={s.text}>
        {title && <strong className={s.title}>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
      {action && <div className={s.action}>{action}</div>}
      {dismiss && <div className={s.dismiss}>{dismiss}</div>}
    </div>
  );
};
